/**
 * The folder delete's instances of the generic job machinery (#781): its
 * tracker, its in-flight rows and its reload record. How a job is followed —
 * socket first, status route as the safety net — is in `job-tracker.ts`.
 */
import type { OperationProgressEvent } from '@papan/realtime/client'
import type { PartitionPreview } from '@papan/types'
import {
  createJobTracker, createInFlightSet, createPendingJobs,
  type JobTracker, type JobTrackerDeps, type JobTrackerTimings, type PendingJob,
} from './job-tracker.js'

/**
 * A finished delete, in `filesApi.deleteFile`'s shape (#801): a WHOLE delete
 * has `binId`/`expiresAt`; a SPLIT one only the lists, and `skipped`.
 */
export interface DeleteJobOutcome {
  binId?: string
  expiresAt?: string
  binIds: string[]
  binnedFileIds: string[]
  skipped?: PartitionPreview
  /** The bin entry is in the source tenant (a delete through a received
   *  share): the person cannot restore it from their own bin. */
  deletedInSource?: boolean
}

export interface DeleteJobProgress {
  phase: NonNullable<OperationProgressEvent['phase']>
  done: number
  total: number
}

export type DeleteTrackerDeps = Omit<JobTrackerDeps<DeleteJobOutcome>, 'kind' | 'readResult'>

export interface DeleteTracker extends Omit<JobTracker<DeleteJobOutcome>, 'follow'> {
  follow: (jobId: string, operationId: string, onProgress?: (p: DeleteJobProgress) => void) => Promise<DeleteJobOutcome>
}

/**
 * A completed WHOLE delete's event carries its bin entry. A SPLIT's does not
 * carry what it skipped (names, on a tenant-wide channel), so it reads as no
 * result and the tracker asks the status route, which answers the requester.
 */
function readDeleteResult(e: OperationProgressEvent): DeleteJobOutcome | null {
  if (e.split || !e.binId || !e.expiresAt) return null
  return {
    binId: e.binId,
    expiresAt: e.expiresAt,
    binIds: e.binIds ?? [e.binId],
    binnedFileIds: e.binnedFileIds ?? [],
    ...(e.deletedInSource ? { deletedInSource: true } : {}),
  }
}

export function createDeleteTracker(
  deps: DeleteTrackerDeps,
  ownOperationId: string,
  timings: JobTrackerTimings = {},
): DeleteTracker {
  // The delete's phases are the only ones a 'delete' event carries.
  return createJobTracker({ ...deps, kind: 'delete', readResult: readDeleteResult }, ownOperationId, timings) as DeleteTracker
}

/** Rows this tab has a Delete in flight for (see `createInFlightSet`). */
export const inFlightDeletes = createInFlightSet()

export interface PendingDelete extends PendingJob {
  fileId: string
  name: string
}

/** Deletes a reload interrupted, per tab. The key predates the generic store. */
export const pendingDeletes = createPendingJobs<PendingDelete>('papan:pending-deletes')
